
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EnhancedProduct } from "@/types/product";
import { convertToProduct } from "@/utils/productTypeUtils";

export const useEnhancedProductData = (identifier: string, isSlug: boolean = false) => {
  return useQuery({
    queryKey: ['enhanced-product', identifier, isSlug],
    queryFn: async (): Promise<EnhancedProduct | null> => {
      if (!identifier) return null;

      // Performance monitoring removed for production

      // Build query based on whether we're looking up by slug or ID
      let query = supabase
        .from('products')
        .select(`
          *,
          categories (*)
        `)
        .eq('status', 'active');

      // Use either url_slug or id based on the identifier type
      if (isSlug) {
        query = query.eq('url_slug', identifier);
      } else {
        query = query.eq('id', identifier);
      }

      const { data: product, error: productError } = await query.maybeSingle();

      if (productError || !product) {
        console.error('Product fetch error:', productError);
        throw new Error('Product not found');
      }

      // Product fetched successfully

      // Convert to our Product type
      const convertedProduct = convertToProduct(product);

      // Fetch product variants from separate table using the actual product ID
      const { data: variants } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', product.id) // Always use the product ID for variants
        .eq('is_active', true);

      // Map variants to our ProductVariant interface with proper type casting
      const mappedVariants = variants?.map(variant => ({
        id: variant.id,
        product_id: variant.product_id,
        name: variant.name || 'Default Variant',
        sku: variant.sku || undefined,
        price: variant.price || undefined,
        compare_at_price: variant.compare_at_price || undefined,
        stock_quantity: variant.stock_quantity || 0,
        weight: variant.weight || undefined,
        variant_options: (variant.variant_options && typeof variant.variant_options === 'object' && !Array.isArray(variant.variant_options)) 
          ? variant.variant_options as Record<string, string>
          : {} as Record<string, string>,
        image_url: variant.image_url || undefined,
        is_active: variant.is_active || true,
        created_at: variant.created_at || new Date().toISOString(),
        updated_at: variant.updated_at || new Date().toISOString(),
      })) || [];

      // Fetch approved reviews with ratings
      const { data: reviews } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', product.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      const mappedReviews = (reviews || []).map(review => ({
        id: review.id,
        product_id: review.product_id,
        user_id: review.user_id || undefined,
        rating: review.rating,
        title: review.title || undefined,
        content: review.content || undefined,
        verified_purchase: review.verified_purchase,
        helpful_count: review.helpful_count,
        is_featured: review.is_featured,
        status: review.status as 'pending' | 'approved' | 'rejected',
        reviewer_name: review.reviewer_name || undefined,
        reviewer_email: review.reviewer_email || undefined,
        created_at: review.created_at,
        updated_at: review.updated_at,
      }));

      // Calculate real ratings
      const reviewCount = mappedReviews.length;
      const averageRating = reviewCount > 0
        ? Number((mappedReviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount).toFixed(1))
        : 0;

      // Other mock data (to be implemented in future phases)
      const mockSpecifications = [];
      const mockImages = convertedProduct.gallery_images ? convertedProduct.gallery_images.map((url: string, index: number) => ({
        id: `img_${index}`,
        product_id: product.id,
        image_url: url,
        alt_text: `${convertedProduct.name} image ${index + 1}`,
        display_order: index,
        is_primary: index === 0,
        image_type: 'gallery' as const,
        created_at: new Date().toISOString()
      })) : [];
      const mockQuestions = [];
      const mockRecommendations = [];
      const mockDiscountRules = [];

      const enhancedProduct: EnhancedProduct = {
        ...convertedProduct,
        variants: mappedVariants,
        specifications: mockSpecifications,
        reviews: mappedReviews,
        images: mockImages,
        questions: mockQuestions,
        recommendations: mockRecommendations,
        discount_rules: mockDiscountRules,
        average_rating: averageRating,
        review_count: reviewCount,
      };

      // Enhanced product data processed
      return enhancedProduct;
    },
    enabled: !!identifier,
  });
};
